import { get, replace, round, startsWith } from 'lodash';

const scrubDimensionUnits = dimension => round(replace(dimension, 'px', ''));
const scrubHexUnits = color => replace(color, '#', '');
const removeLastComma = string => string.replace(/,\s*$/, '');

/**
 *
 * Image abstraction, allowing integration with Cloudinary
 *
 * @param {String} src Image source url
 * @param {Object} transformations Optional object holding Cloudinary transformations to apply to image.
 * @param {String} transformations.width Image width transformation (eg '123px').
 * @param {String} transformations.height Image height transformation (eg '123px').
 * @param {String} transformations.bgColor Image background color transformation hex (eg '#ffffff').
 * @param {String} transformations.format Image format transormation (eg 'jpeg'). Defaults to 'auto'.
 * @param {String} transformations.quality Image quality transormation (eg '60'). Defaults to 'auto'.
 * @param {Object} config Cloudinary configuration
 * @param {Boolean} config.isEnabled Whether Cloudinary is enabled or disabled
 * @param {String} config.baseUrl Cloudinary base URL
 */
const CloudinaryRemoteFetchImage = class {
  constructor(src, transformations = {}, config = window._cloudinary) {
    this._src = src;
    this._transformations = transformations;
    this._config = config;
  }

  static getSrc = (...args) => {
    const image = new CloudinaryRemoteFetchImage(...args);
    return image.src;
  };

  // Setters

  set transformations(transformations) {
    this._transformations = transformations;
  }

  // Transformations

  get width() {
    return get(this._transformations, 'width');
  }

  get height() {
    return get(this._transformations, 'height');
  }

  get bgColor() {
    return get(this._transformations, 'bgColor');
  }

  get format() {
    return get(this._transformations, 'format', 'auto');
  }

  get quality() {
    return get(this._transformations, 'quality', 'auto');
  }

  get gravity() {
    return get(this._transformations, 'gravity');
  }

  get crop() {
    return get(this._transformations, 'crop');
  }

  get aspectRatio() {
    return get(this._transformations, 'aspectRatio');
  }

  // Config

  get isCloudinaryEnabled() {
    return get(this._config, 'isEnabled', false);
  }

  get cloudinaryBaseUrl() {
    return get(this._config, 'baseUrl');
  }

  // Transformations

  get widthParam() {
    if (!this.width) {
      return '';
    }
    const width = scrubDimensionUnits(this.width);
    return `w_${width},`;
  }

  get heightParam() {
    if (!this.height) {
      return '';
    }
    const height = scrubDimensionUnits(this.height);
    return `h_${height},`;
  }

  get formatParam() {
    return `f_${this.format},`;
  }

  get qualityParam() {
    return `q_${this.quality},`;
  }

  get bgColorParam() {
    if (!this.bgColor) {
      return '';
    }
    const bgColor = scrubHexUnits(this.bgColor);
    return `b_rgb:${bgColor},`;
  }

  get gravityParam() {
    if (!this.gravity) {
      return '';
    }

    return `g_${this.gravity},`;
  }

  get cropParam() {
    if (!this.crop) {
      return '';
    }

    return `c_${this.crop},`;
  }

  get aspectRatioParam() {
    if (!this.aspectRatio) {
      return '';
    }

    return `ar_${this.aspectRatio},`;
  }

  // Url Parts

  get transformationsUrl() {
    const transformationsUrl = `${this.widthParam}${this.heightParam}${this.formatParam}${this.qualityParam}${this.bgColorParam}${this.gravityParam}${this.cropParam}${this.aspectRatioParam}`;
    return removeLastComma(transformationsUrl);
  }

  get sourceUrl() {
    let src = this._src;
    if (startsWith(src, '//')) {
      src = `https:${src}`;
    }

    return src;
  }

  get remoteFetchUrl() {
    return `${this.cloudinaryBaseUrl}${this.transformationsUrl}/${this.sourceUrl}`;
  }

  get src() {
    return this.isCloudinaryEnabled ? this.remoteFetchUrl : this.sourceUrl;
  }
};

export default CloudinaryRemoteFetchImage;
